/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminStudentMetricData } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import Spinner from '@design-system/spinner';
import Chip from '@design-system/chip';
import StudentItem from '@component/admin/student/student-home/student-list/student-item';

interface IProps {
  selectedDate: string;
  students: IAdminStudentMetricData[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  studied: boolean;
  handleRetry: () => void;
  handleStudiedChange: (studied: boolean) => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  padding: 16px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 6px;
  align-items: center;
`;

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NextLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${color.BLACK_5};
`;

const Sentinel = styled.div`
  height: 1px;
`;

const StudentList: React.FC<IProps> = ({
  selectedDate,
  students,
  isLoading,
  isFetchingNextPage,
  isError,
  studied,
  handleRetry,
  handleStudiedChange,
  sentinelRef,
}) => {
  return (
    <Container>
      <Header>
        <TextBlock
          text={`${selectedDate} 학습자`}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
        />
        <FilterGroup>
          <Chip
            text='전체'
            size='small'
            isSelected={!studied}
            handleClick={() => handleStudiedChange(false)}
            theme={ETheme.BROWN}
          />
          <Chip
            text='학습'
            size='small'
            isSelected={studied}
            handleClick={() => handleStudiedChange(true)}
            theme={ETheme.BROWN}
          />
        </FilterGroup>
      </Header>
      {isLoading ? (
        <Loader>
          <Spinner size='32px' color={color.BROWN_1} />
        </Loader>
      ) : isError ? (
        <Empty>
          데이터를 불러오지 못했습니다.{' '}
          <span
            style={{ color: color.BROWN_1, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleRetry}>
            다시 시도
          </span>
        </Empty>
      ) : students.length === 0 ? (
        <Empty>학습자가 없습니다.</Empty>
      ) : (
        <>
          <List>
            {students.map((s) => (
              <StudentItem key={s.studentKey} student={s} />
            ))}
          </List>
          {isFetchingNextPage && (
            <NextLoader>
              <Spinner size='24px' color={color.BROWN_1} />
            </NextLoader>
          )}
          <Sentinel ref={sentinelRef} />
        </>
      )}
    </Container>
  );
};

export default StudentList;